(() => {
  'use strict';
  if (window.URCommerceLoaded) return;
  window.URCommerceLoaded = true;
  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  function setup(form) {
    if (form.dataset.ready) return;
    form.dataset.ready = 'true';
    const status = form.querySelector('[data-ur-status]');
    const submit = form.querySelector('[data-ur-submit]');
    const restart = form.querySelector('[data-ur-restart]');
    const fields = form.querySelector('fieldset');
    const storageKey = `ur-inquiry:${location.pathname}:${form.id}`;
    const preview = form.dataset.mode !== 'connected';
    let pending = null;
    let busy = false;
    let completed = false;
    const say = (message, state = '') => { status.textContent = message; status.dataset.state = state; };
    const save = value => {
      // Never persist names, emails, notes or image bytes in browser storage.
      try { value ? sessionStorage.setItem(storageKey, JSON.stringify(value)) : sessionStorage.removeItem(storageKey); }
      catch { say('Dein Browser erlaubt keine Zwischenspeicherung. Bitte lasse diese Seite bis zur Bestätigung geöffnet.'); }
    };
    let previous = null;
    try { previous = preview ? null : JSON.parse(sessionStorage.getItem(storageKey) || 'null'); } catch { previous = null; }
    if (previous && (!UUID.test(previous.id) || !/^[0-9a-f]{64}$/.test(previous.hash))) previous = null;
    if (!crypto.randomUUID || !crypto.subtle) {
      say('Bitte öffne den Shop in einem aktuellen Browser über HTTPS, um deine Anfrage zu senden.', 'error');
      return;
    }
    submit.disabled = false;
    if (previous) say('Eine frühere Anfrage wurde noch nicht bestätigt. Mit denselben Angaben kannst du den Versand sicher wiederholen.');
    async function fingerprint(data) {
      const entries = [];
      for (const [key, value] of data) {
        if (value instanceof File) {
          const bytes = await value.arrayBuffer();
          const hash = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)), b => b.toString(16).padStart(2, '0')).join('');
          entries.push([key, hash]);
        } else entries.push([key, value]);
      }
      return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(entries)))), b => b.toString(16).padStart(2, '0')).join('');
    }
    restart.addEventListener('click', () => {
      if (busy) return;
      if (!completed && !window.confirm('Die vorherige Anfrage könnte bereits angekommen sein. Möchtest du trotzdem eine neue Anfrage beginnen?')) return;
      pending = null; previous = null; completed = false; save(null); fields.disabled = false;
      form.reset(); restart.hidden = true; submit.disabled = false; submit.textContent = 'Unverbindlich anfragen'; say('');
    });
    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (busy || completed || (!pending && !form.reportValidity())) return;
      busy = true; submit.disabled = true;
      try {
        if (!pending) {
          const data = new FormData(form);
          const phoneDigits = String(data.get('phone') || '').replace(/\D/g, '');
          if (phoneDigits.length < 7 || phoneDigits.length > 15) throw new Error('Bitte gib eine gültige Telefon- oder Handynummer an.');
          const photos = data.getAll('photos').filter(file => file instanceof File && file.size);
          if (photos.length > 5 || photos.some(file => file.size > 10 * 1024 * 1024)) throw new Error('Bitte wähle höchstens 5 Fotos mit jeweils maximal 10 MB.');
          if (preview) {
            say('Formularprüfung erfolgreich. Entwurfsmodus: Es wurde keine Anfrage versendet oder im Backend gespeichert.');
            status.focus();
            return;
          }
          data.delete('photos'); photos.forEach(file => data.append('photos', file));
          if (data.has('scope')) {
            const scope = data.get('scope');
            data.set('hasBox', String(scope === 'fullset' || scope === 'box'));
            data.set('hasPapers', String(scope === 'fullset' || scope === 'papers'));
            if (scope === 'unknown') data.set('notes', `${data.get('notes') || ''}\nLieferumfang: noch ungeklärt.`.trim());
            data.delete('scope');
          }
          data.set('sourcePage', location.pathname.slice(0, 500));
          // Prices use whole EUR in the existing backend contract.
          const hash = await fingerprint(data);
          if (previous && previous.hash !== hash) {
            restart.hidden = false;
            throw new Error('Diese Angaben unterscheiden sich von der noch unbestätigten Anfrage. Nutze dieselben Angaben oder beginne ausdrücklich eine neue Anfrage.');
          }
          const id = previous?.id || crypto.randomUUID();
          data.set('requestId', id);
          pending = { data, id, hash };
          previous = { id, hash }; save(previous);
        }
        fields.disabled = true;
        say('Deine Anfrage wird übermittelt …');
        const endpoint = new URL(form.dataset.endpoint, location.origin);
        if (endpoint.origin !== location.origin || !endpoint.pathname.startsWith('/apps/ur-luxury/')) throw new Error('Der Anfrageweg ist nicht verfügbar. Bitte kontaktiere uns über die Angaben im Shop.');
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 25000);
        let response;
        let receipt;
        try {
          response = await fetch(endpoint, { method: 'POST', body: pending.data, headers: { Accept: 'application/json', 'X-Idempotency-Key': pending.id }, credentials: 'same-origin', signal: controller.signal });
          receipt = await response.json().catch(() => null);
        } finally { clearTimeout(timer); }
        if (response.ok && receipt?.status === 'ok' && UUID.test(receipt.id || '')) {
          completed = true; save(null);
          say(`Deine Anfrage ist angekommen. Wir melden uns persönlich bei dir. Deine Anfragenummer: ${receipt.deal_code || receipt.id}.`, 'success');
          submit.textContent = 'Anfrage erhalten'; restart.hidden = false; status.focus();
          return;
        }
        if ([413, 415, 422].includes(response.status)) {
          pending = null; previous = null; save(null); fields.disabled = false;
          if (receipt?.error === 'phone_required') throw new Error('Bitte gib eine gültige Telefon- oder Handynummer an.');
          throw new Error(response.status === 413 ? 'Die Fotos sind zu groß. Bitte verkleinere sie und versuche es erneut.' : 'Bitte prüfe deine Angaben und Fotos. Die Anfrage wurde nicht angenommen. Erlaubt sind JPEG, PNG, WebP und unterstützte HEIC-Fotos.');
        }
        if (response.status === 429) throw new Error('Der Anfrageweg ist gerade ausgelastet. Bitte warte einige Minuten und wiederhole den Versand.');
        throw new Error('Der Eingang ist noch nicht bestätigt. Bitte wiederhole den Versand mit denselben Angaben. Dadurch entsteht keine doppelte Anfrage.');
      } catch (error) {
        say(error instanceof TypeError || error.name === 'AbortError' ? 'Die Verbindung wurde unterbrochen. Der Eingang ist noch nicht bestätigt. Bitte wiederhole den Versand; deine Angaben bleiben erhalten.' : error.message, 'error');
        submit.textContent = preview ? 'Formular testen' : pending ? 'Versand erneut versuchen' : 'Unverbindlich anfragen';
        restart.hidden = !pending && !previous;
      } finally { busy = false; submit.disabled = completed; }
    });
  }
  function initialize(root = document) { root.querySelectorAll('[data-ur-inquiry]').forEach(setup); }
  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
